/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useClient } from 'shared/apollo';
import { ENV } from 'shared/utils/constants';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import TrueImpactConfirmationDialog from 'shared/design-system/components/TrueImpactConfirmationDialog';
import TrueImpactFloatLoading from 'shared/design-system/components/TrueImpactFloatLoading';
import {
  RequestState,
  requestError,
  requestInit,
  requestStart,
  requestSuccess,
  setErrorResponse,
} from 'shared/auth/hooks/useRequestState';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFlask,
  faTrash,
  faTriangleExclamation,
  faCopy,
  faPlus,
} from '@fortawesome/pro-light-svg-icons';
import {
  CIAccount,
  CICleanupSummary,
  CITestPair,
  ciAddInvestmentToDonor,
  ciCleanupTestAccount,
  ciCreateTestAccounts,
  getCITestAccounts,
} from './api';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PageWrapper = styled.div`
  width: 100%;
  max-width: 1300px;
  margin: 0 auto;
  padding: ${SPACERS.large};
  box-sizing: border-box;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACERS.small};
  margin-bottom: ${SPACERS.medium};
  .header-icon {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background: ${COLORS.ctaPrimaryBackground};
    color: ${COLORS.ctaPrimary};
  }
  .heading {
    margin: 0;
  }
  .subheading {
    margin: 2px 0 0;
    color: ${COLORS.grayDark};
  }
`;

const Card = styled.div`
  background: ${COLORS.whiteBase};
  border: 1px solid ${COLORS.greyLight};
  border-radius: 12px;
  padding: ${SPACERS.medium};
  margin-bottom: ${SPACERS.medium};
  box-shadow: 0 2px 8px rgba(0, 115, 163, 0.05);
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr auto;
  gap: ${SPACERS.small};
  align-items: end;
  margin-top: ${SPACERS.small};
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
  .field-label {
    display: block;
    margin-bottom: 4px;
    color: ${COLORS.grayDarkest};
  }
`;

const ErrorBanner = styled.div`
  background: ${COLORS.criticalBackground};
  color: ${COLORS.critical};
  border: 1px solid ${COLORS.critical};
  border-radius: 8px;
  padding: ${SPACERS.small} ${SPACERS.medium};
  margin-top: ${SPACERS.small};
  white-space: pre-wrap;
  word-break: break-word;
`;

const ResultBox = styled.div`
  margin-top: ${SPACERS.medium};
  padding: ${SPACERS.small} ${SPACERS.medium};
  background: ${COLORS.successBackground};
  border: 1px solid ${COLORS.success};
  border-radius: 8px;
  .result-title {
    margin-bottom: ${SPACERS.tiny};
    color: ${COLORS.success};
  }
  .result-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${SPACERS.small};
    @media (max-width: 720px) {
      grid-template-columns: 1fr;
    }
  }
  .kv-row {
    display: flex;
    align-items: center;
    gap: ${SPACERS.tiny};
    padding: 4px 0;
    .k {
      color: ${COLORS.grayDarkest};
      font-weight: 600;
      min-width: 110px;
    }
    .v {
      font-family: monospace;
      color: ${COLORS.blackBase};
      word-break: break-all;
      flex: 1;
    }
    .copy-btn {
      cursor: pointer;
      background: transparent;
      border: none;
      color: ${COLORS.ctaPrimary};
      padding: 2px 6px;
      &:hover {
        opacity: 0.7;
      }
    }
  }
  .sub-card {
    background: ${COLORS.whiteBase};
    border: 1px solid ${COLORS.greyLight};
    border-radius: 8px;
    padding: ${SPACERS.small};
  }
`;

const StyledTableContainer = styled(TableContainer)`
  border: 1px solid ${COLORS.greyLight};
  border-radius: 12px;
  background: ${COLORS.whiteBase};
  .mono {
    font-family: monospace;
    font-size: 12px;
  }
  .row-donor {
    background: ${COLORS.whiteBase};
  }
  .row-npo {
    background: ${COLORS.greyLightest};
  }
`;

const RolePill = styled.span<{ roleLabel: 'donor' | 'npo' }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  background: ${({ roleLabel }) =>
    roleLabel === 'donor'
      ? COLORS.ctaBrandBackground
      : COLORS.ctaPrimaryBackground};
  color: ${({ roleLabel }) =>
    roleLabel === 'donor' ? COLORS.ctaBrand : COLORS.ctaPrimary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${SPACERS.extraLarge} ${SPACERS.large};
  color: ${COLORS.grayDark};
  background: ${COLORS.greyLightest};
  border-radius: 12px;
  border: 1px dashed ${COLORS.greyBase};
  .icon {
    font-size: 40px;
    color: ${COLORS.grayBase};
    margin-bottom: ${SPACERS.small};
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${SPACERS.medium};
`;

const Gate = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${SPACERS.extraLarge};
  color: ${COLORS.grayDark};
`;

const formatGqlError = (err: any): string => {
  if (!err) return 'Unknown error.';
  const gqlErrors = err.graphQLErrors || err?.networkError?.result?.errors;
  if (gqlErrors && gqlErrors.length) {
    return gqlErrors.map((e: any) => e.message).join('\n');
  }
  return err.message || String(err);
};

const isAuthOrEnvError = (err: any): boolean => {
  const msg = formatGqlError(err).toLowerCase();
  return msg.includes('forbidden') || msg.includes('unauthorized');
};

const copyToClipboard = (text: string) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    navigator.clipboard.writeText(text).catch(() => { /* noop */ });
  }
};

interface KVProps {
  label: string;
  value: string | number | null | undefined;
  copyable?: boolean;
}

const KV: React.FC<KVProps> = ({ label, value, copyable }) => {
  const text = value === null || value === undefined ? '' : String(value);
  return (
    <div className="kv-row">
      <span className="k">{label}</span>
      <span className="v">{text || '—'}</span>
      {copyable && text ? (
        <button
          type="button"
          className="copy-btn"
          title="Copy"
          onClick={() => copyToClipboard(text)}
        >
          <FontAwesomeIcon icon={faCopy} />
        </button>
      ) : null}
    </div>
  );
};

interface CreatedResult {
  ticks: string;
  password: string;
  portfolioId: number | null;
  programId: number | null;
  investmentId: number | null;
  invitationId: number | null;
  donor: CIAccount;
  npo: CIAccount;
}

interface CreateFormProps {
  onCreated: (result: CreatedResult) => void;
}

const CreateForm: React.FC<CreateFormProps> = ({ onCreated }) => {
  const client = useClient();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedEmail = email.trim();
  const emailValid = EMAIL_REGEX.test(trimmedEmail);

  const handleSubmit = async () => {
    if (!client || !emailValid) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await ciCreateTestAccounts(client, {
        email: trimmedEmail,
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
      });
      const data = res.data?.ciCreateTestAccounts;
      if (!data || !data.success) {
        setError('Server returned an unsuccessful response.');
        return;
      }
      onCreated(data);
      setEmail('');
      setFirstName('');
      setLastName('');
    } catch (err) {
      setError(formatGqlError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <TrueImpactText variant="heading-subheading">
        Create test accounts
      </TrueImpactText>
      <TrueImpactText
        variant="body-small"
        color="grayDark"
        style={{ display: 'block', marginTop: 4 }}
      >
        Creates a donor + NPO pair (with portfolio, program, investment,
        invitation) using an email. Password: <strong>Ci-Test1!</strong>
      </TrueImpactText>
      <FormGrid>
        <div>
          <TrueImpactText className="field-label" variant="body-small-bold">
            Email *
          </TrueImpactText>
          <TrueImpactTextfield
            placeholder="test@test.com"
            value={email}
            onChange={v => setEmail(v)}
            isError={!!trimmedEmail && !emailValid}
          />
        </div>
        <div>
          <TrueImpactText className="field-label" variant="body-small-bold">
            First name
          </TrueImpactText>
          <TrueImpactTextfield
            placeholder="CI"
            value={firstName}
            onChange={v => setFirstName(v)}
          />
        </div>
        <div>
          <TrueImpactText className="field-label" variant="body-small-bold">
            Last name
          </TrueImpactText>
          <TrueImpactTextfield
            placeholder="Tester"
            value={lastName}
            onChange={v => setLastName(v)}
          />
        </div>
        <TrueImpactButton
          variant="primary-small"
          disabled={!emailValid || submitting}
          onClick={handleSubmit}
          style={{ height: '53.78px' }}
        >
          {submitting ? 'Creating…' : 'Create pair'}
        </TrueImpactButton>
      </FormGrid>
      {error && <ErrorBanner>{error}</ErrorBanner>}
    </Card>
  );
};

interface ResultPanelProps {
  result: CreatedResult;
  onDismiss: () => void;
}

const AccountResultCard: React.FC<{ label: string; account: CIAccount }> = ({
  label,
  account,
}) => (
  <div className="sub-card">
    <TrueImpactText variant="body-bold">{label}</TrueImpactText>
    <KV label="Email" value={account.email} copyable />
    <KV label="Password" value={account.password || ''} copyable />
    <KV label="Account ID" value={account.accountId} copyable />
    <KV label="Account name" value={account.accountName} />
    <KV label="Account type" value={account.accountType} />
    <KV label="User ID" value={account.userId} copyable />
    <KV label="First name" value={account.firstName} />
    <KV label="Last name" value={account.lastName} />
    <KV label="Role" value={account.kind} />
    <KV label="Ticks" value={account.ticks} copyable />
    <KV label="Created by" value={account.createdBy} />
  </div>
);

const ResultPanel: React.FC<ResultPanelProps> = ({ result, onDismiss }) => (
  <ResultBox>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <TrueImpactText variant="heading-subheading" className="result-title">
        Test accounts created
      </TrueImpactText>
      <TrueImpactButton variant="secondary-small" onClick={onDismiss}>
        Dismiss
      </TrueImpactButton>
    </div>
    <div style={{ marginTop: SPACERS.small }}>
      <KV label="Password" value={result.password} copyable />
      <KV label="Portfolio ID" value={result.portfolioId} copyable />
      <KV label="Program ID" value={result.programId} copyable />
      <KV label="Investment ID" value={result.investmentId} copyable />
      <KV label="Invitation ID" value={result.invitationId} copyable />
    </div>
    <div className="result-grid" style={{ marginTop: SPACERS.small }}>
      <AccountResultCard label="Donor" account={result.donor} />
      <AccountResultCard label="NPO" account={result.npo} />
    </div>
  </ResultBox>
);

interface RowProps {
  pair: CITestPair;
  account: CIAccount;
  roleLabel: 'donor' | 'npo';
  onDelete: (account: CIAccount, pair: CITestPair) => void;
  onAddInvestment?: (account: CIAccount) => void;
  busyEmail: string | null;
}

const AccountRow: React.FC<RowProps> = ({
  pair,
  account,
  roleLabel,
  onDelete,
  onAddInvestment,
  busyEmail,
}) => {
  const busy = busyEmail === account.email;
  const isFunder = roleLabel === 'donor' || account.accountType === 'funder';
  return (
    <TableRow className={`row-${roleLabel}`} hover>
      <TableCell>
        <RolePill roleLabel={roleLabel}>{roleLabel}</RolePill>
      </TableCell>
      <TableCell className="mono" title={account.email}>
        {account.email}
      </TableCell>
      <TableCell>
        {[account.firstName, account.lastName].filter(Boolean).join(' ') || '—'}
      </TableCell>
      <TableCell title={account.accountName}>{account.accountName}</TableCell>
      <TableCell className="mono" align="right">
        {account.accountId}
      </TableCell>
      <TableCell className="mono" align="right">
        {account.userId}
      </TableCell>
      <TableCell title={pair.createdBy || ''}>
        {pair.createdBy || account.createdBy || '—'}
      </TableCell>
      <TableCell align="right">
        <div
          style={{
            display: 'inline-flex',
            gap: 8,
            justifyContent: 'flex-end',
          }}
        >
          {isFunder && onAddInvestment && (
            <TrueImpactButton
              variant="secondary-small"
              onClick={() => onAddInvestment(account)}
              disabled={busy}
            >
              <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />
              Add Investment
            </TrueImpactButton>
          )}
          <TrueImpactButton
            variant="secondary-small"
            onClick={() => onDelete(account, pair)}
            disabled={busy}
            color="critical"
          >
            <FontAwesomeIcon icon={faTrash} style={{ marginRight: 6 }} />
            {busy ? 'Deleting…' : 'Delete'}
          </TrueImpactButton>
        </div>
      </TableCell>
    </TableRow>
  );
};

interface AddInvestmentDialogProps {
  open: boolean;
  donor: CIAccount | null;
  onClose: () => void;
  onSuccess: (successMessage: string) => void;
  onError: (errorMessage: string) => void;
  onLoading: () => void;
  onFatal: (message: string) => void;
}

const AddInvestmentDialog: React.FC<AddInvestmentDialogProps> = ({
  open,
  donor,
  onClose,
  onSuccess,
  onError,
  onLoading,
  onFatal,
}) => {
  const client = useClient();
  const [npoEmail, setNpoEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      const donorEmail = donor?.email || '';
      const suggested = donorEmail.replace(/donor/gi, match =>
        match === match.toUpperCase() ? 'NPO' : 'npo',
      );
      setNpoEmail(suggested);
      setAmount('');
      setError(null);
      setSubmitting(false);
    }
  }, [open, donor]);

  const trimmedEmail = npoEmail.trim();
  const emailValid = EMAIL_REGEX.test(trimmedEmail);
  const amountNum = parseFloat(amount);
  const amountValid = !isNaN(amountNum) && amountNum >= 0.01;
  const canSubmit = !!donor && emailValid && amountValid && !submitting;

  const handleSubmit = async () => {
    if (!client || !donor || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    onLoading();
    try {
      const res = await ciAddInvestmentToDonor(client, {
        donorAccountId: donor.accountId,
        npoEmail: trimmedEmail,
        amount: amountNum,
      });
      const data = res.data?.ciAddInvestmentToDonor;
      if (!data || !data.success) {
        const msg = 'Server returned an unsuccessful response.';
        setError(msg);
        onError(msg);
        return;
      }
      onSuccess(
        `Investment #${data.investmentId ?? '?'} added to ${donor.accountName}`,
      );
      onClose();
    } catch (err) {
      const msg = formatGqlError(err);
      if (
        isAuthOrEnvError(err) ||
        /CI endpoints|CI test endpoints/i.test(msg)
      ) {
        onFatal(msg);
        onClose();
        return;
      }
      setError(msg);
      onError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <TrueImpactConfirmationDialog
      open={open}
      handleClose={onClose}
      previous={{ text: 'Cancel', onClick: onClose }}
      next={{
        text: submitting ? 'Adding…' : 'Add Investment',
        onClick: handleSubmit,
        disabled: !canSubmit,
      }}
    >
      <TrueImpactText variant="heading-subheading">
        Add Investment to Donor
      </TrueImpactText>
      {donor && (
        <div style={{ marginTop: SPACERS.small }}>
          <KV label="Donor" value={donor.accountName} />
          <KV label="Account ID" value={donor.accountId} />
        </div>
      )}
      <div style={{ marginTop: SPACERS.medium }}>
        <TrueImpactText variant="body-small-bold">NPO Email *</TrueImpactText>
        <TrueImpactTextfield
          placeholder="grantee@example.org"
          value={npoEmail}
          onChange={v => setNpoEmail(v)}
          isError={!!trimmedEmail && !emailValid}
        />
        <TrueImpactText
          variant="body-small"
          color="grayDark"
          style={{ display: 'block', marginTop: 4 }}
        >
          An existing NPO/grantee account must already exist for this email.
        </TrueImpactText>
      </div>
      <div style={{ marginTop: SPACERS.small }}>
        <TrueImpactText variant="body-small-bold">Amount *</TrueImpactText>
        <TrueImpactTextfield
          placeholder="0.00"
          type="number"
          value={amount}
          onChange={v => setAmount(v)}
          isError={!!amount && !amountValid}
        />
      </div>
      {error && <ErrorBanner>{error}</ErrorBanner>}
    </TrueImpactConfirmationDialog>
  );
};

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const CITests: React.FC = () => {
  const client = useClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<CITestPair[]>([]);
  const [total, setTotal] = useState(0);
  const [gated, setGated] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [createdResult, setCreatedResult] = useState<CreatedResult | null>(
    null,
  );
  const [pendingDelete, setPendingDelete] = useState<{
    account: CIAccount;
    pair: CITestPair;
  } | null>(null);
  const [busyEmail, setBusyEmail] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [investmentDonor, setInvestmentDonor] = useState<CIAccount | null>(
    null,
  );
  const [addInvestmentState, setAddInvestmentState] = useState<
    RequestState<{ message: string }>
  >(requestInit());
  const [addInvestmentMessage, setAddInvestmentMessage] = useState<string>('');
  const [
    lastCleanupSummary,
    setLastCleanupSummary,
  ] = useState<CICleanupSummary | null>(null);

  const refresh = useCallback(async () => {
    if (!client) return;
    if (ENV === 'prod' || ENV === 'uat') {
      setGated('disallowed');
      setLoading(false);
      return;
    }
    setLoading(true);
    setListError(null);
    try {
      const { data } = await getCITestAccounts(client, { page, pageSize });
      const payload = data?.ciTestAccounts;
      setItems(payload?.items || []);
      setTotal(payload?.total || 0);
      setGated(null);
    } catch (err) {
      if (isAuthOrEnvError(err)) {
        setGated(formatGqlError(err));
      } else {
        setListError(formatGqlError(err));
      }
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [client, page, pageSize]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleDelete = async () => {
    if (!client || !pendingDelete) return;
    const email = pendingDelete.account.email;
    setBusyEmail(email);
    setDeleteError(null);
    setLastCleanupSummary(null);
    try {
      const res = await ciCleanupTestAccount(client, email);
      const summary = res.data?.ciCleanupTestAccount?.summary || null;
      setLastCleanupSummary(summary);
      setPendingDelete(null);
      await refresh();
    } catch (err) {
      setDeleteError(formatGqlError(err));
    } finally {
      setBusyEmail(null);
    }
  };

  if (gated) {
    return (
      <PageWrapper>
        <Gate>
          <TrueImpactText>
            CI Tests is unavailable in this environment or for this user.
          </TrueImpactText>
        </Gate>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <PageHeader>
        <div className="header-icon">
          <FontAwesomeIcon icon={faFlask} size="lg" />
        </div>
        <div>
          <TrueImpactText variant="heading-page" className="heading">
            CI Tests
          </TrueImpactText>
          <TrueImpactText className="subheading">
            Provision and clean up CI test donor/NPO account pairs.
          </TrueImpactText>
        </div>
      </PageHeader>

      <CreateForm onCreated={setCreatedResult} />

      {createdResult && (
        <ResultPanel
          result={createdResult}
          onDismiss={() => setCreatedResult(null)}
        />
      )}

      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: SPACERS.small,
          }}
        >
          <TrueImpactText variant="heading-subheading">
            Existing CI test accounts
          </TrueImpactText>
          <TrueImpactText variant="body-small" color="grayDark">
            {total} pair{total === 1 ? '' : 's'}
          </TrueImpactText>
        </div>

        {listError && <ErrorBanner>{listError}</ErrorBanner>}
        {deleteError && <ErrorBanner>{deleteError}</ErrorBanner>}
        {lastCleanupSummary && (
          <TrueImpactText
            variant="body-small"
            color="grayDark"
            style={{ display: 'block', marginBottom: SPACERS.small }}
          >
            Last cleanup: accounts={lastCleanupSummary.accounts}, users=
            {lastCleanupSummary.users}, portfolios=
            {lastCleanupSummary.portfolios}, programs=
            {lastCleanupSummary.programs}, investments=
            {lastCleanupSummary.investments}, invitations=
            {lastCleanupSummary.invitations}, allocations=
            {lastCleanupSummary.allocations}, reports=
            {lastCleanupSummary.reports}.
          </TrueImpactText>
        )}

        {loading && items.length === 0 ? (
          <LoadingWrapper>
            <TrueImpactLoading />
          </LoadingWrapper>
        ) : items.length === 0 ? (
          <EmptyState>
            <FontAwesomeIcon icon={faFlask} className="icon" />
            <TrueImpactText>
              No CI test accounts yet. Create your first pair above.
            </TrueImpactText>
          </EmptyState>
        ) : (
          <StyledTableContainer
            style={{ opacity: loading ? 0.6 : 1, transition: '0.2s' }}
          >
            <MuiTable size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Role</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Account name</TableCell>
                  <TableCell align="right">Account ID</TableCell>
                  <TableCell align="right">User ID</TableCell>
                  <TableCell>Created by</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(pair => (
                  <React.Fragment key={pair.ticks}>
                    {pair.donor && (
                      <AccountRow
                        pair={pair}
                        account={pair.donor}
                        roleLabel="donor"
                        onDelete={(a, p) =>
                          setPendingDelete({ account: a, pair: p })
                        }
                        onAddInvestment={a => setInvestmentDonor(a)}
                        busyEmail={busyEmail}
                      />
                    )}
                    {pair.npo && (
                      <AccountRow
                        pair={pair}
                        account={pair.npo}
                        roleLabel="npo"
                        onDelete={(a, p) =>
                          setPendingDelete({ account: a, pair: p })
                        }
                        busyEmail={busyEmail}
                      />
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </MuiTable>
          </StyledTableContainer>
        )}

        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          onPageChange={(_e, newPage) => setPage(newPage + 1)}
          rowsPerPage={pageSize}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onRowsPerPageChange={e => {
            setPage(1);
            setPageSize(parseInt(e.target.value, 10));
          }}
        />
      </Card>

      <TrueImpactFloatLoading
        requestState={addInvestmentState}
        loadingMessage="Adding investment…"
        successMessage={addInvestmentMessage || 'Investment added.'}
        errorMessage={addInvestmentMessage || 'Failed to add investment.'}
      />

      <AddInvestmentDialog
        open={!!investmentDonor}
        donor={investmentDonor}
        onClose={() => setInvestmentDonor(null)}
        onLoading={() => {
          setAddInvestmentMessage('');
          setAddInvestmentState(requestStart());
        }}
        onSuccess={msg => {
          setAddInvestmentMessage(msg);
          setAddInvestmentState(requestSuccess({ message: msg }));
          refresh();
        }}
        onError={msg => {
          setAddInvestmentMessage(msg);
          setAddInvestmentState(
            requestError(setErrorResponse({ status: 400, message: msg })),
          );
        }}
        onFatal={msg => {
          setGated(msg);
        }}
      />

      <TrueImpactConfirmationDialog
        open={!!pendingDelete}
        icon={faTriangleExclamation}
        iconColor="critical"
        handleClose={() => setPendingDelete(null)}
        previous={{
          text: 'Cancel',
          onClick: () => setPendingDelete(null),
        }}
        next={{
          text: busyEmail ? 'Deleting…' : 'Delete',
          onClick: handleDelete,
        }}
      >
        <TrueImpactText variant="heading-subheading">
          Delete CI test account?
        </TrueImpactText>
        <TrueImpactText style={{ marginTop: SPACERS.small, display: 'block' }}>
          This will hard-delete <b>{pendingDelete?.account.email}</b> and all
          associated data (portfolios/programs/investments/invitations/
          allocations/reports as applicable). This cannot be undone.
        </TrueImpactText>
      </TrueImpactConfirmationDialog>
    </PageWrapper>
  );
};

export default CITests;
